import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Client,
  Measurement,
  OutfitType,
  SubscriptionInfo,
  SubscriptionPlan,
  UserAccount,
  AuthResponse,
  SyncMeta,
  SyncClientPayload,
  SyncMeasurementPayload,
  SyncOrderPayload,
  ClientDraft,
  MeasurementDraft,
  Gender,
  Order,
  OrderStatus,
} from './types';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useNotifications } from './hooks/useNotifications';
import { useTheme } from './hooks/useTheme';
import { ClientList } from './components/ClientList';
import { ClientDetails } from './components/ClientDetails';
import { ClientForm } from './components/ClientForm';
import { MeasurementForm } from './components/MeasurementForm';
import { OrderForm } from './components/OrderForm';
import { PlusIcon, ArrowLeftIcon, TapeMeasureIcon, SearchIcon, MenuIcon, TruckIcon } from './components/icons';
import { FilterChips } from './components/mobile/FilterChips';
import AboutPage from './components/AboutPage';
import { WelcomeModal } from './components/WelcomeModal';
import { InstallBanner } from './components/InstallBanner';
import AuthView from './components/auth/AuthView';
import { ProfileSettings } from './components/ProfileSettings';
import { Dashboard } from './components/Dashboard';
import { AdminDashboard } from './components/AdminDashboard';
import type { OrderFormValues } from './components/OrderForm';
import AppHeader from './components/AppHeader';
import { BottomNav, MobileTab } from './components/mobile/BottomNav';
import { GalleryView } from './components/mobile/GalleryView';
import { OrderListView } from './components/mobile/OrderListView';
import { deleteLocalImage, saveImageLocally } from './utils/imageDb';
import { getConfiguredAuthProvider, loginWithConfiguredProvider, registerWithConfiguredProvider } from './utils/authProvider';

const MAX_FREE_CLIENTS = 3;
const MAX_FREE_ORDERS = 3;
const SYNC_INTERVAL_DAYS = 7;
const SYNC_ENDPOINT = '/fedapay_integration/sync.php';

// Limites de clients par plan
const PLAN_CLIENT_LIMITS: Record<string, number> = {
  free: 3,
  starter: 50,
  essentiel: 200,
  pro: 1000,
  premium: -1, // illimite
};

const PLAN_LABELS: Record<string, string> = {
  free: 'Gratuit',
  starter: 'Starter',
  essentiel: 'Essentiel',
  pro: 'Pro',
  premium: 'Premium',
};

// Verifie si l'abonnement est expire localement (meme hors-ligne)
const isSubscriptionExpiredLocally = (activeUntil: string | null): boolean => {
  if (!activeUntil) return false;
  try {
    const expirationDate = new Date(activeUntil);
    const now = new Date();
    return now > expirationDate;
  } catch {
    return false;
  }
};

const RESERVED_MEASUREMENT_KEYS = new Set(['id', 'outfitType', 'date', 'notes', 'updatedAt', 'isDeleted']);

type SyncStatus = 'idle' | 'syncing' | 'success' | 'error';

const parseLooseJson = <T,>(raw: string): T | null => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    const objectStart = trimmed.indexOf('{');
    const arrayStart = trimmed.indexOf('[');
    const startCandidates = [objectStart, arrayStart].filter((index) => index >= 0);
    if (startCandidates.length === 0) {
      throw new Error('Aucune charge JSON detectee dans la reponse serveur.');
    }

    const start = Math.min(...startCandidates);
    const objectEnd = trimmed.lastIndexOf('}');
    const arrayEnd = trimmed.lastIndexOf(']');
    const end = Math.max(objectEnd, arrayEnd);
    if (end < start) {
      throw new Error('La reponse serveur contient un JSON incomplet.');
    }

    return JSON.parse(trimmed.slice(start, end + 1)) as T;
  }
};

const readJsonResponse = async <T,>(response: Response): Promise<T | null> => {
  const raw = await response.text();
  return parseLooseJson<T>(raw);
};

const isLocalImageReference = (value: string | null | undefined): value is `local_${string}` => {
  return typeof value === 'string' && value.startsWith('local_');
};

const getRemoteImageReferences = (items: string[] | null | undefined): string[] => {
  return (items ?? []).filter((item) => !isLocalImageReference(item));
};

const mergeRemoteAndLocalImageReferences = (
  remoteItems: string[] | null | undefined,
  existingItems: string[] | null | undefined
): string[] => {
  const remote = getRemoteImageReferences(remoteItems);
  const existingLocal = (existingItems ?? []).filter(isLocalImageReference);
  const merged = [...remote];
  existingLocal.forEach((item) => {
    if (!merged.includes(item)) {
      merged.push(item);
    }
  });
  return merged;
};

const shouldFallbackToLocalImageStorage = (
  statusCode: number,
  payload: { status?: string; allowed?: boolean; message?: string } | null
): boolean => {
  // Si le backend refuse l'auth (401/403), on garde l'app utilisable en local.
  if (statusCode === 401 || statusCode === 403) {
    return true;
  }
  if (statusCode === 409) {
    return true;
  }
  if (payload?.status === 'deny' || payload?.allowed === false) {
    return true;
  }
  const message = String(payload?.message ?? '').toLowerCase();
  return (
    message.includes('stockage') ||
    message.includes('espace') ||
    message.includes('quota') ||
    message.includes('volumineux') ||
    message.includes('insuffisant') ||
    message.includes('plein')
  );
};

const toIsoString = (value?: string | number | Date): string => {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }
  return new Date().toISOString();
};

const normaliseGender = (value: string | null | undefined, fallback: Client['gender']): Client['gender'] => {
  if (value === undefined || value === null) {
    return fallback;
  }
  const trimmed = value.trim();
  if (trimmed.toLowerCase() === 'homme') {
    return Gender.Homme;
  }
  if (trimmed.toLowerCase() === 'femme') {
    return Gender.Femme;
  }
  return fallback;
};

const extractMeasurementValues = (measurement: Measurement): Record<string, string | number | boolean | string[]> => {
  const values: Record<string, string | number | boolean | string[]> = {};
  Object.keys(measurement).forEach((key) => {
    if (!RESERVED_MEASUREMENT_KEYS.has(key)) {
      const value = measurement[key];
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        values[key] = value;
      } else if (Array.isArray(value)) {
        const cleanedValues = value
          .map((entry) => String(entry).trim())
          .filter((entry) => entry !== '');
        if (cleanedValues.length > 0) {
          values[key] = cleanedValues;
        }
      }
    }
  });
  return values;
};

const mapMeasurementFromSync = (
  payload: SyncMeasurementPayload,
  existingLocalAttachments: string[] = []
): Measurement => {
  const values = payload.values || {};
  return {
    id: payload.id,
    outfitType: (payload.outfitType as OutfitType) ?? OutfitType.Haut,
    date: payload.date ?? new Date().toISOString(),
    notes: payload.notes ?? undefined,
    attachments: mergeRemoteAndLocalImageReferences(payload.attachments ?? [], existingLocalAttachments),
    updatedAt: payload.updatedAt,
    isDeleted: payload.isDeleted,
    ...values,
  };
};

const mapClientsToSyncPayload = (clients: Client[]): SyncClientPayload[] => {
  return clients.map((client) => ({
    id: client.id,
    name: client.name ?? null,
    gender: client.gender ?? null,
    contact: client.contact ?? null,
    images: getRemoteImageReferences(client.images ?? []),
    updatedAt: client.updatedAt,
    isDeleted: !!client.isDeleted,
    measurements: (client.measurements || []).map((measurement) => ({
      id: measurement.id,
      clientId: client.id,
      outfitType: measurement.outfitType ?? null,
      date: measurement.date ?? null,
      notes: measurement.notes ?? null,
      values: extractMeasurementValues(measurement),
      attachments: getRemoteImageReferences(measurement.attachments ?? []),
      updatedAt: measurement.updatedAt,
      isDeleted: !!measurement.isDeleted,
    })),
  }));
};

const mapOrdersToSyncPayload = (orders: Order[]): SyncOrderPayload[] => {
  return orders.map((order) => ({
    id: order.id,
    clientId: order.clientId,
    measurementId: order.measurementId ?? null,
    title: order.title,
    status: order.status,
    outfitType: order.outfitType ?? null,
    negotiatedAmount: order.negotiatedAmount ?? null,
    paid: !!order.paid,
    dueDate: order.dueDate ?? null,
    deliveredAt: order.deliveredAt ?? null,
    notes: order.notes ?? null,
    attachments: getRemoteImageReferences(order.attachments ?? []),
    updatedAt: order.updatedAt,
    isDeleted: !!order.isDeleted,
  }));
};

const mergeMeasurements = (
  localMeasurements: Measurement[],
  incoming: SyncMeasurementPayload[]
): Measurement[] => {
  const measurementMap = new Map<string, Measurement>();
  localMeasurements.forEach((measurement) => {
    measurementMap.set(measurement.id, { ...measurement });
  });

  incoming.forEach((payload) => {
    const existing = measurementMap.get(payload.id);
    const incomingUpdated = Date.parse(payload.updatedAt);
    if (existing) {
      const existingUpdated = Date.parse(existing.updatedAt);
      if (Number.isNaN(existingUpdated) || incomingUpdated > existingUpdated) {
        measurementMap.set(payload.id, mapMeasurementFromSync(payload, existing.attachments ?? []));
      }
    } else {
      measurementMap.set(payload.id, mapMeasurementFromSync(payload));
    }
  });

  return Array.from(measurementMap.values());
};

const mergeClientsFromSync = (localClients: Client[], incoming: SyncClientPayload[]): Client[] => {
  const clientMap = new Map<string, Client>();
  localClients.forEach((client) => {
    clientMap.set(client.id, {
      ...client,
      measurements: client.measurements ? client.measurements.map((m) => ({ ...m })) : [],
    });
  });

  incoming.forEach((payload) => {
    const existing = clientMap.get(payload.id);
    const incomingUpdated = Date.parse(payload.updatedAt);
    if (existing) {
      const existingUpdated = Date.parse(existing.updatedAt);
      if (Number.isNaN(existingUpdated) || incomingUpdated > existingUpdated) {
        const mergedMeasurements = mergeMeasurements(existing.measurements || [], payload.measurements || []);
        clientMap.set(payload.id, {
          ...existing,
          name: payload.name ?? existing.name,
          gender: normaliseGender(payload.gender, existing.gender),
          contact: payload.contact ?? existing.contact,
          images: mergeRemoteAndLocalImageReferences(payload.images ?? [], existing.images ?? []),
          updatedAt: payload.updatedAt,
          isDeleted: payload.isDeleted,
          measurements: mergedMeasurements,
        });
      }
    } else {
      const mergedMeasurements = mergeMeasurements([], payload.measurements || []);
      clientMap.set(payload.id, {
        id: payload.id,
        name: payload.name ?? '',
        gender: normaliseGender(payload.gender, Gender.Femme),
        contact: payload.contact ?? undefined,
        images: payload.images ?? undefined,
        updatedAt: payload.updatedAt,
        isDeleted: payload.isDeleted,
        measurements: mergedMeasurements,
      });
    }
  });

  return Array.from(clientMap.values());
};

const mergeOrdersFromSync = (localOrders: Order[], incoming: SyncOrderPayload[]): Order[] => {
  const orderMap = new Map<string, Order>();
  localOrders.forEach((order) => {
    orderMap.set(order.id, { ...order });
  });

  incoming.forEach((payload) => {
    const existing = orderMap.get(payload.id);
    const incomingUpdated = Date.parse(payload.updatedAt);
    const nextOrder: Order = {
      id: payload.id,
      clientId: payload.clientId,
      measurementId: payload.measurementId ?? null,
      title: payload.title,
      status: (payload.status as OrderStatus) ?? 'pending',
      outfitType: (payload.outfitType as OutfitType) ?? null,
      negotiatedAmount: payload.negotiatedAmount ?? null,
      paid: !!payload.paid,
      dueDate: payload.dueDate ?? null,
      deliveredAt: payload.deliveredAt ?? null,
      notes: payload.notes ?? null,
      attachments: mergeRemoteAndLocalImageReferences(payload.attachments ?? [], existing?.attachments ?? []),
      createdAt: existing?.createdAt,
      updatedAt: payload.updatedAt,
      isDeleted: payload.isDeleted,
    };

    if (!existing) {
      orderMap.set(payload.id, nextOrder);
      return;
    }

    const existingUpdated = Date.parse(existing.updatedAt);
    if (Number.isNaN(existingUpdated) || incomingUpdated > existingUpdated) {
      orderMap.set(payload.id, { ...existing, ...nextOrder });
    }
  });

  return Array.from(orderMap.values());
};

const defaultSubscription: SubscriptionInfo = {
  client_count: 0,
  subscription_status: 'free',
  active_until: null,
  plan: 'free',
};

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const App: React.FC = () => {
  const configuredAuthProvider = getConfiguredAuthProvider();
  const [authToken, setAuthToken] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : window.localStorage.getItem('cms-auth-token')
  );
  const [currentUser, setCurrentUser] = useState<UserAccount | null>(null);
  const { isDark, toggleTheme } = useTheme();

  const clientStorageKey = currentUser ? `tailor-clients-${currentUser.userId}` : 'tailor-clients-guest';
  const [clients, setClients] = useLocalStorage<Client[]>(clientStorageKey, []);
  const orderStorageKey = currentUser ? `tailor-orders-${currentUser.userId}` : 'tailor-orders-guest';
  const [orders, setOrders] = useLocalStorage<Order[]>(orderStorageKey, []);
  const syncMetaKey = currentUser ? `tailor-sync-meta-${currentUser.userId}` : 'tailor-sync-meta-guest';
  const [syncMeta, setSyncMeta] = useLocalStorage<SyncMeta>(syncMetaKey, { lastSyncAt: null, lastAttemptAt: null });
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncFailures, setSyncFailures] = useState(0);

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [isTakingMeasurements, setIsTakingMeasurements] = useState(false);
  const [measurementToEdit, setMeasurementToEdit] = useState<Measurement | null>(null);
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  const [authChecked, setAuthChecked] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(() => {
    const hasSeenWelcome = localStorage.getItem('cms-welcome-shown');
    return !hasSeenWelcome;
  });

  // Profile settings modal state
  const [showProfileSettings, setShowProfileSettings] = useState(false);

  // Dashboard state
  const [showDashboard, setShowDashboard] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  const [activeTab, setActiveTab] = useState<MobileTab>('clients');
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    // Check for admin URL parameter
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('admin') === 'true') {
        setShowAdmin(true);
      }
    }
  }, []);

  // Créer une map des clients pour les notifications
  const clientsMap = useMemo(() => {
    const map = new Map<string, string>();
    clients.filter(c => !c.isDeleted).forEach(c => map.set(c.id, c.name));
    return map;
  }, [clients]);

  const paymentRefreshTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const normalisedOnceRef = useRef(false);
  const hasShownLocalStorageFallbackRef = useRef(false);
  const deferredInstallPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [isInstallPromptAvailable, setIsInstallPromptAvailable] = useState(false);
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const safeUserId = currentUser?.userId ?? null;

  const uploadFileForCurrentUser = useCallback(
    async (file: File): Promise<string> => {
      if (!safeUserId) {
        throw new Error("Utilisateur non authentifie. Veuillez vous reconnecter.");
      }
      const saveFileLocally = async (): Promise<string> => {
        const localId = `local_${safeUserId}_${Date.now()}_${crypto.randomUUID()}`;
        await saveImageLocally(localId, file);
        if (!hasShownLocalStorageFallbackRef.current) {
          hasShownLocalStorageFallbackRef.current = true;
          alert("Serveur indisponible ou stockage limite: image enregistree localement sur cet appareil. Elle restera visible ici, mais ne sera pas synchronisee sur les autres appareils.");
        }
        return localId;
      };

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      const authorizeResponse = await fetch('/api/upload_authorize.php', {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_id: safeUserId, file_size_bytes: file.size }),
      });

      const authorizeData = await authorizeResponse.json().catch(() => null);
      if (!authorizeResponse.ok || authorizeData?.status !== 'ok' || authorizeData?.allowed === false) {
        if (shouldFallbackToLocalImageStorage(authorizeResponse.status, authorizeData)) {
          return saveFileLocally();
        }
        const message = authorizeData?.message || "Autorisation d'upload refusee.";
        throw new Error(message);
      }

      const formData = new FormData();
      formData.append('user_id', safeUserId);
      formData.append('file', file);

      const uploadHeaders: Record<string, string> = {};
      if (authToken) {
        uploadHeaders.Authorization = `Bearer ${authToken}`;
      }

      const uploadResponse = await fetch('/api/upload.php', {
        method: 'POST',
        headers: uploadHeaders,
        body: formData,
      });

      const uploadData = await uploadResponse.json().catch(() => null);
      if (!uploadResponse.ok || uploadData?.status !== 'ok' || !uploadData?.file_url) {
        if (shouldFallbackToLocalImageStorage(uploadResponse.status, uploadData)) {
          return saveFileLocally();
        }
        const message = uploadData?.message || "Echec de l'upload d'image.";
        throw new Error(message);
      }

      return uploadData.file_url as string;
    },
    [safeUserId, authToken]
  );

  const deleteFileForCurrentUser = useCallback(
    async (fileUrl: string): Promise<void> => {
      console.info(`[App] Tentative de suppression du fichier: ${fileUrl}`);
      if (isLocalImageReference(fileUrl)) {
        await deleteLocalImage(fileUrl);
        return;
      }
      if (!safeUserId) {
        throw new Error("Utilisateur non authentifie. Veuillez vous reconnecter.");
      }

      const fileNameEncoded = fileUrl.split('/').pop();
      if (!fileNameEncoded) {
        throw new Error("Nom de fichier introuvable.");
      }
      const fileName = decodeURIComponent(fileNameEncoded);

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      const response = await fetch('/api/delete_image.php', {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_id: safeUserId, file_name: fileName }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || data?.status !== 'ok') {
        const message = data?.message || "Echec de suppression du fichier.";
        console.error(`[App] Erreur suppression: ${message}`, data);
        throw new Error(message);
      }
      console.info(`[App] Fichier supprim avec succs: ${fileName}`);
    },
    [safeUserId, authToken]
  );

  const handleAddClientImage = useCallback(
    async (clientId: string, file: File) => {
      try {
        const fileUrl = await uploadFileForCurrentUser(file);
        const updatedAt = new Date().toISOString();
        let nextClient: Client | null = null;

        setClients((prev) =>
          prev.map((client) => {
            if (client.id !== clientId) {
              return client;
            }
            const images = [...(client.images ?? []), fileUrl];
            const updatedClient: Client = { ...client, images, updatedAt };
            nextClient = updatedClient;
            return updatedClient;
          })
        );

        if (nextClient) {
          setSelectedClient((prev) => (prev && prev.id === clientId ? nextClient : prev));
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Ajout d'image impossible.";
        alert(message);
        throw error instanceof Error ? error : new Error(message);
      }
    },
    [setClients, uploadFileForCurrentUser]
  );

  const handleRemoveClientImage = useCallback(
    async (clientId: string, imageUrl: string) => {
      try {
        console.info(`[App] Suppression de l'image pour le client ${clientId}: ${imageUrl}`);
        await deleteFileForCurrentUser(imageUrl);
        let nextClient: Client | null = null;
        const updatedAt = new Date().toISOString();

        setClients((prev) =>
          prev.map((client) => {
            if (client.id !== clientId) {
              return client;
            }
            const images = (client.images ?? []).filter((url) => url !== imageUrl);
            const updatedClient: Client = { ...client, images, updatedAt };
            nextClient = updatedClient;
            return updatedClient;
          })
        );

        if (nextClient) {
          setSelectedClient((prev) => (prev && prev.id === clientId ? nextClient : prev));
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Suppression impossible.";
        console.error(`[App] Echec handleRemoveClientImage:`, error);
        alert(message);
        throw error instanceof Error ? error : new Error(message);
      }
    },
    [deleteFileForCurrentUser, setClients, setSelectedClient]
  );

  const goToSubscriptionPage = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.href = '/abonnement/';
    }
  }, []);

  const handleSaveOrder = useCallback(
    async (clientId: string, orderData: OrderFormValues) => {
      const nowIso = new Date().toISOString();
      if (orderData.id) {
        setOrders((prev) =>
          prev.map((order) => {
            if (order.id !== orderData.id) {
              return order;
            }
            const nextAttachments =
              orderData.attachments !== undefined ? orderData.attachments ?? [] : order.attachments;
            return {
              ...order,
              title: orderData.title,
              status: orderData.status ?? order.status,
              outfitType: orderData.outfitType ?? null,
              negotiatedAmount:
                orderData.negotiatedAmount != null ? Number(orderData.negotiatedAmount) : null,
              paid: !!orderData.paid,
              dueDate: orderData.dueDate ?? null,
              deliveredAt: orderData.deliveredAt ?? order.deliveredAt ?? null,
              notes: orderData.notes ?? null,
              attachments: nextAttachments ?? [],
              updatedAt: nowIso,
            } as Order;
          })
        );
      } else {
        const planFromSession = (subscriptionInfo?.plan ?? currentUser?.subscription?.plan ?? 'free') as SubscriptionPlan;
        if (planFromSession === 'free') {
          const activeOrderCount = orders.filter((order) => !order.isDeleted).length;
          if (activeOrderCount >= MAX_FREE_ORDERS) {
            const shouldUpgrade = window.confirm(
              `Limite de ${MAX_FREE_ORDERS} commandes pour le forfait gratuit. Passez au forfait superieur pour continuer. Aller a la page abonnement ?`
            );
            if (shouldUpgrade) {
              goToSubscriptionPage();
            }
            return;
          }
        }
        const attachments = orderData.attachments ?? [];
        const status = orderData.status ?? 'pending';
        const newOrder: Order = {
          id: crypto.randomUUID(),
          clientId,
          title: orderData.title,
          status,
          outfitType: orderData.outfitType ?? null,
          negotiatedAmount:
            orderData.negotiatedAmount != null ? Number(orderData.negotiatedAmount) : null,
          paid: !!orderData.paid,
          dueDate: orderData.dueDate ?? null,
          deliveredAt: orderData.deliveredAt ?? null,
          notes: orderData.notes ?? null,
          attachments,
          createdAt: nowIso,
          updatedAt: nowIso,
          isDeleted: false,
        };
        setOrders((prev) => [...prev, newOrder]);
      }
    },
    [currentUser, goToSubscriptionPage, orders, setOrders, subscriptionInfo]
  );

  const handleDeleteOrder = useCallback(
    async (orderId: string) => {
      const nowIso = new Date().toISOString();
      setOrders((prev) =>
        prev.map((order) => (order.id === orderId ? { ...order, isDeleted: true, updatedAt: nowIso } : order))
      );
    },
    [setOrders]
  );

  const handleChangeOrderStatus = useCallback(
    async (orderId: string, status: OrderStatus) => {
      const nowIso = new Date().toISOString();
      const today = new Date().toISOString().slice(0, 10);
      setOrders((prev) =>
        prev.map((order) => {
          if (order.id !== orderId) {
            return order;
          }
          const deliveredAt = status === 'delivered' ? order.deliveredAt ?? today : null;
          return {
            ...order,
            status,
            deliveredAt,
            updatedAt: nowIso,
          };
        })
      );
    },
    [setOrders]
  );

  const handleToggleOrderPaid = useCallback(
    async (orderId: string, nextPaid: boolean) => {
      const nowIso = new Date().toISOString();
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId
            ? {
              ...order,
              paid: nextPaid,
              updatedAt: nowIso,
            }
            : order
        )
      );
    },
    [setOrders]
  );
  const [showAbout, setShowAbout] = useState(false);

  const isiOS = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }
    return /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
  }, []);

  useEffect(() => {
    if (normalisedOnceRef.current) {
      return;
    }
    normalisedOnceRef.current = true;
    let changed = false;
    const nowIso = new Date().toISOString();
    const normalised = clients.map((client) => {
      let clientChanged = false;
      const clientUpdatedAt = client.updatedAt ?? nowIso;
      if (!client.updatedAt) {
        clientChanged = true;
      }
      const safeName = typeof client.name === 'string' ? client.name : '';
      if (client.name !== safeName) {
        clientChanged = true;
      }
      const safeContact = typeof client.contact === 'string' ? client.contact : undefined;
      if (client.contact !== safeContact) {
        clientChanged = true;
      }
      const clientIsDeleted = client.isDeleted ?? false;
      if (client.isDeleted === undefined) {
        clientChanged = true;
      }
      const measurements = (client.measurements || []).map((measurement) => {
        const measurementUpdatedAt = measurement.updatedAt ?? clientUpdatedAt;
        const measurementIsDeleted = measurement.isDeleted ?? false;
        if (!measurement.updatedAt || measurement.isDeleted === undefined) {
          clientChanged = true;
        }
        return {
          ...measurement,
          updatedAt: measurementUpdatedAt,
          isDeleted: measurementIsDeleted,
        };
      });
      if (clientChanged) {
        changed = true;
      }
      return {
        ...client,
        updatedAt: clientUpdatedAt,
        name: safeName,
        contact: safeContact,
        isDeleted: clientIsDeleted,
        measurements,
      };
    });
    if (changed) {
      setClients(normalised);
    }
  }, [clients, setClients]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const nav = window.navigator as Navigator & { standalone?: boolean };
    const updateStandalone = () => {
      const mediaMatches = typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches;
      const iosStandalone = typeof nav.standalone === 'boolean' ? nav.standalone : false;
      setIsStandalone(mediaMatches || iosStandalone);
    };

    updateStandalone();

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      const promptEvent = event as BeforeInstallPromptEvent;
      deferredInstallPromptRef.current = promptEvent;
      setIsInstallPromptAvailable(true);
      setShowInstallInstructions(false);
    };

    const handleAppInstalled = () => {
      deferredInstallPromptRef.current = null;
      setIsInstallPromptAvailable(false);
      setShowInstallInstructions(false);
      updateStandalone();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    const mediaQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(display-mode: standalone)') : null;
    const handleMediaChange = () => updateStandalone();

    if (mediaQuery) {
      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', handleMediaChange);
      } else if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(handleMediaChange);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (mediaQuery) {
        if (typeof mediaQuery.removeEventListener === 'function') {
          mediaQuery.removeEventListener('change', handleMediaChange);
        } else if (typeof mediaQuery.removeListener === 'function') {
          mediaQuery.removeListener(handleMediaChange);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (isStandalone) {
      deferredInstallPromptRef.current = null;
      setIsInstallPromptAvailable(false);
      setShowInstallInstructions(false);
    }
  }, [isStandalone]);

  const handleInstallClick = useCallback(async () => {
    const promptEvent = deferredInstallPromptRef.current;

    if (promptEvent) {
      try {
        await promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        console.info('[PWA] Choix utilisateur installation:', choice.outcome);
      } catch (error) {
        console.error('[PWA] Echec lors du prompt dinstallation', error);
      } finally {
        deferredInstallPromptRef.current = null;
        setIsInstallPromptAvailable(false);
        setShowInstallInstructions(false);
      }
      return;
    }

    if (isiOS && !isStandalone) {
      setShowInstallInstructions(true);
    }
  }, [isiOS, isStandalone]);

  const closeInstallInstructions = useCallback(() => {
    setShowInstallInstructions(false);
  }, []);
  const effectiveSubscription = useMemo<SubscriptionInfo>(() => {
    return subscriptionInfo ?? currentUser?.subscription ?? { ...defaultSubscription };
  }, [subscriptionInfo, currentUser]);

  const activeClients = useMemo(() => clients.filter((client) => !client.isDeleted), [clients]);
  const activeOrders = useMemo(() => orders.filter((order) => !order.isDeleted), [orders]);

  const selectedClientForView = selectedClient
    ? {
      ...selectedClient,
      measurements: (selectedClient.measurements || []).filter((measurement) => !measurement.isDeleted),
    }
    : null;

  const filterCounts = useMemo(() => {
    const counts = { all: activeClients.length, to_deliver: 0, unpaid: 0, new: 0 };
    const now = Date.now();

    activeClients.forEach(client => {
      const clientOrders = activeOrders.filter(o => o.clientId === client.id);
      if (clientOrders.some(o => o.status !== 'delivered')) counts.to_deliver++;
      if (clientOrders.some(o => !o.paid)) counts.unpaid++;
      const updatedAt = client.updatedAt ? new Date(client.updatedAt).getTime() : 0;
      if (now - updatedAt < 24 * 60 * 60 * 1000) counts.new++;
    });

    return counts;
  }, [activeClients, activeOrders]);

  const searchTerm = (searchQuery ?? '').toLowerCase();
  const filteredClients = useMemo(() => {
    const now = Date.now();
    return activeClients
      .filter((client) => {
        const name = typeof client.name === 'string' ? client.name.toLowerCase() : '';
        const contact = typeof client.contact === 'string' ? client.contact.toLowerCase() : '';
        const matchesSearch = name.includes(searchTerm) || contact.includes(searchTerm);
        if (!matchesSearch) return false;

        if (activeFilter === 'all') return true;
        const clientOrders = activeOrders.filter(o => o.clientId === client.id);
        if (activeFilter === 'to_deliver') return clientOrders.some(o => o.status !== 'delivered');
        if (activeFilter === 'unpaid') return clientOrders.some(o => !o.paid);
        if (activeFilter === 'new') {
          const updatedAt = client.updatedAt ? new Date(client.updatedAt).getTime() : 0;
          return now - updatedAt < 24 * 60 * 60 * 1000;
        }
        return true;
      })
      .sort((a, b) => {
        const nameA = typeof a.name === 'string' ? a.name : '';
        const nameB = typeof b.name === 'string' ? b.name : '';
        return nameA.localeCompare(nameB);
      });
  }, [activeClients, activeOrders, searchTerm, activeFilter]);

  const activeClientCount = activeClients.length;
  const subscriptionClientCount = Math.max(effectiveSubscription.client_count, activeClientCount);
  const remainingClients = Math.max(0, MAX_FREE_CLIENTS - subscriptionClientCount);
  // Verification locale de l'expiration (fonctionne meme hors-ligne)
  const isExpiredLocally = isSubscriptionExpiredLocally(effectiveSubscription.active_until);
  const actualSubscriptionStatus = isExpiredLocally ? 'expired' : effectiveSubscription.subscription_status;

  const needsSubscriptionPayment =
    actualSubscriptionStatus === 'expired' ||
    (actualSubscriptionStatus === 'free' && subscriptionClientCount >= MAX_FREE_CLIENTS);

  // Afficher le bouton "S'abonner" dans ces cas :
  // 1. Plan gratuit (peut souscrire)
  // 2. Abonnement expiré (doit renouveler)
  // 3. Abonnement bientôt expiré (7 jours avant expiration)
  // 4. Utilisateur veut upgrader (plan non-premium)
  const isNearExpiration = (() => {
    if (!effectiveSubscription.active_until) return false;
    try {
      const expirationDate = new Date(effectiveSubscription.active_until);
      const now = new Date();
      const daysUntilExpiration = (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
      return daysUntilExpiration > 0 && daysUntilExpiration <= 7;
    } catch {
      return false;
    }
  })();

  const subscriptionPlan: SubscriptionPlan = (effectiveSubscription.plan ?? 'free') as SubscriptionPlan;

  const canUpgradeEarly =
    actualSubscriptionStatus === 'free' ||  // Utilisateur gratuit
    actualSubscriptionStatus === 'expired' ||  // Abonnement expiré
    isNearExpiration ||  // Expire dans 7 jours
    (subscriptionPlan !== 'premium' && subscriptionPlan !== 'pro');  // Peut upgrader

  const paymentCustomer = activeClients[0]
    ? { name: activeClients[0].name, contact: activeClients[0].contact }
    : undefined;
  const showInstallCta = !isStandalone && (isInstallPromptAvailable || (isiOS && !showInstallInstructions));

  const planAllowsOrders = subscriptionPlan === 'pro' || subscriptionPlan === 'premium' || subscriptionPlan === 'free';
  const ordersEnabled = (actualSubscriptionStatus === 'active' || actualSubscriptionStatus === 'free') && planAllowsOrders;

  // Notifications push pour les échéances de commandes
  const { checkDeadlines } = useNotifications({
    orders: activeOrders,
    enabled: ordersEnabled && currentUser !== null,
    clientsMap,
    authToken,
    userId: currentUser?.userId,
  });

  // Vérifier les notifications manquées au démarrage de l'app
  useEffect(() => {
    if (ordersEnabled && currentUser !== null) {
      checkDeadlines();
    }
  }, [ordersEnabled, currentUser, checkDeadlines]);

  const ordersForSelectedClient = useMemo(() => {
    const clientId = selectedClient?.id;
    if (!clientId) {
      return [] as Order[];
    }
    return activeOrders.filter((order) => order.clientId === clientId);
  }, [activeOrders, selectedClient?.id]);


  const latestLocalUpdate = useMemo(() => {
    let latestTimestamp = 0;
    clients.forEach((client) => {
      const clientTime = Date.parse(client.updatedAt ?? '');
      if (!Number.isNaN(clientTime)) {
        latestTimestamp = Math.max(latestTimestamp, clientTime);
      }
      (client.measurements || []).forEach((measurement) => {
        const measurementTime = Date.parse(measurement.updatedAt ?? '');
        if (!Number.isNaN(measurementTime)) {
          latestTimestamp = Math.max(latestTimestamp, measurementTime);
        }
      });
    });
    orders.forEach((order) => {
      const orderTime = Date.parse(order.updatedAt ?? '');
      if (!Number.isNaN(orderTime)) {
        latestTimestamp = Math.max(latestTimestamp, orderTime);
      }
    });
    return latestTimestamp === 0 ? null : new Date(latestTimestamp).toISOString();
  }, [clients, orders]);

  const hasUnsyncedChanges = useMemo(() => {
    if (!latestLocalUpdate) {
      return clients.length > 0 || orders.length > 0;
    }
    if (!syncMeta.lastSyncAt) {
      return true;
    }
    const latestLocal = Date.parse(latestLocalUpdate);
    const lastSync = Date.parse(syncMeta.lastSyncAt);
    if (Number.isNaN(latestLocal) || Number.isNaN(lastSync)) {
      return true;
    }
    return latestLocal > lastSync;
  }, [clients.length, orders.length, latestLocalUpdate, syncMeta.lastSyncAt]);

  const updateClientCountOnServer = useCallback(
    async (count: number) => {
      if (!currentUser) {
        return;
      }
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
        }
        const response = await fetch('/fedapay_integration/update_client_count.php', {
          method: 'POST',
          headers,
          body: JSON.stringify({ userId: currentUser.userId, clientCount: count }),
        });
        if (!response.ok) {
          console.error('Erreur serveur lors de la mise a jour du nombre de clients:', await response.text());
        }
      } catch (error) {
        console.error('Erreur lors de la mise a jour du nombre de clients:', error);
      }
    },
    [currentUser, authToken]
  );

  const resetUiState = useCallback(() => {
    setSelectedClient(null);
    setIsAddingClient(false);
    setIsTakingMeasurements(false);
    setMeasurementToEdit(null);
    setClientToEdit(null);
  }, []);

  const persistToken = useCallback((token: string | null) => {
    if (typeof window === 'undefined') {
      return;
    }
    if (token) {
      window.localStorage.setItem('cms-auth-token', token);
    } else {
      window.localStorage.removeItem('cms-auth-token');
    }
  }, []);

  const clearAuth = useCallback(
    (removeToken = false) => {
      if (removeToken) {
        setAuthToken(null);
        persistToken(null);
      }
      setSyncStatus('idle');
      setSyncError(null);
      setSyncMeta({ lastSyncAt: null, lastAttemptAt: null });
      setCurrentUser(null);
      setSubscriptionInfo(null);
      setSubscriptionError(null);
      setOrders([]);
      resetUiState();
    },
    [persistToken, resetUiState, setOrders, setSyncMeta]
  );

  const applyAuth = useCallback(
    (payload: AuthResponse) => {
      setAuthToken(payload.token);
      persistToken(payload.token);
      const subscriptionPayload = payload.user.subscription ?? { ...defaultSubscription };
      const subscription: SubscriptionInfo = { ...subscriptionPayload, plan: subscriptionPayload.plan ?? 'free' };
      setCurrentUser({ ...payload.user, subscription });
      setSubscriptionInfo(subscription);
      setAuthError(null);
      resetUiState();
    },
    [persistToken, resetUiState]
  );

  const fetchSubscriptionStatus = useCallback(
    async (userIdOverride?: string) => {
      const targetId = userIdOverride ?? currentUser?.userId;
      if (!targetId) {
        return false;
      }
      // Avoid noisy 401 "Token d'authentification manquant" during startup races.
      if (!authToken) {
        return false;
      }
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      let isActive = false;
      try {
        const headers: Record<string, string> = {};
        if (authToken) {
          headers.Authorization = `Bearer ${authToken}`;
        }
        const response = await fetch('/fedapay_integration/get_subscription_status.php', { headers });
        const data = await readJsonResponse<any>(response);

        if (response.ok && data?.status === 'ok') {
          const planCandidate = typeof data.subscription_plan === 'string' ? data.subscription_plan : undefined;
          const allowedPlans: SubscriptionPlan[] = ['free', 'starter', 'essentiel', 'pro', 'premium'];
          const rawPlan = planCandidate && allowedPlans.includes(planCandidate as SubscriptionPlan)
            ? (planCandidate as SubscriptionPlan)
            : undefined;
          const subscription: SubscriptionInfo = {
            client_count: Number(data.client_count ?? 0),
            subscription_status: data.subscription_status ?? 'free',
            active_until: data.active_until ?? null,
            plan: rawPlan ?? 'free',
          };
          setSubscriptionInfo(subscription);
          setCurrentUser((prev) => (prev ? { ...prev, subscription } : prev));
          isActive = subscription.subscription_status === 'active';
        } else {
          if (response.status === 401) {
            // Session/token issue is handled by global auth check; keep UI clean here.
            setSubscriptionError(null);
            return false;
          }
          const message =
            data?.message || `Erreur lors de la recuperation du statut d'abonnement (code ${response.status}).`;
          setSubscriptionError(message);
        }
      } catch (error) {
        console.error("Erreur de recuperation du statut d'abonnement:", error);
        setSubscriptionError("Impossible de se connecter au serveur d'abonnement.");
      } finally {
        setSubscriptionLoading(false);
      }
      return isActive;
    },
    [authToken, currentUser?.userId]
  );

  const performSync = useCallback(
    async (options?: { manual?: boolean }) => {
      if (!currentUser || !authToken) {
        return;
      }
      // Réinitialiser les échecs si manuel
      if (options?.manual) {
        setSyncFailures(0);
      }

      setSyncStatus('syncing');
      setSyncError(null);
      const payload = {
        lastSync: syncMeta.lastSyncAt,
        clients: mapClientsToSyncPayload(clients),
        orders: mapOrdersToSyncPayload(orders),
      };
      try {
        const response = await fetch(SYNC_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(payload),
        });
        const data = await readJsonResponse<any>(response);
        if (!response.ok || data?.status !== 'ok') {
          throw new Error(data?.message || `Synchronisation impossible (code ${response.status}).`);
        }
        const mergedClients = mergeClientsFromSync(clients, data.clients ?? []);
        const cleanedClients = mergedClients
          .map((client) => ({
            ...client,
            measurements: (client.measurements || []).filter((measurement) => !measurement.isDeleted),
          }))
          .filter((client) => !client.isDeleted);
        setClients(cleanedClients);
        const mergedOrders = mergeOrdersFromSync(orders, data.orders ?? []);
        const cleanedOrders = mergedOrders.filter((order) => !order.isDeleted);
        setOrders(cleanedOrders);
        const activeCount = cleanedClients.filter((client) => !client.isDeleted).length;
        const nextSubscription: SubscriptionInfo = { ...effectiveSubscription, client_count: activeCount };
        setSubscriptionInfo(nextSubscription);
        setCurrentUser((prev) => (prev ? { ...prev, subscription: nextSubscription } : prev));
        updateClientCountOnServer(activeCount);
        const syncTimestamp = data.lastSync ?? new Date().toISOString();
        setSyncMeta({
          lastSyncAt: syncTimestamp,
          lastAttemptAt: new Date().toISOString(),
        });

        // Success: Reset failures and idle
        setSyncFailures(0);
        setSyncStatus('success');
        setTimeout(() => setSyncStatus('idle'), 3000);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erreur de synchronisation.';
        setSyncError(message);
        setSyncStatus('error');
        setSyncMeta({
          lastSyncAt: syncMeta.lastSyncAt,
          lastAttemptAt: new Date().toISOString(),
        });

        // Exponential backoff
        // 1st fail: 5s, 2nd: 10s, 3rd: 20s... max 5min
        setSyncFailures((prev) => {
          const next = prev + 1;
          const delay = Math.min(300000, 5000 * Math.pow(2, next - 1));
          console.warn(`[Sync] Echec #${next}. Nouvelle tentative dans ${delay}ms.`);
          setTimeout(() => setSyncStatus('idle'), delay);
          return next;
        });
      }
    },
    [
      authToken,
      clients,
      currentUser,
      effectiveSubscription,
      orders,
      setClients,
      setCurrentUser,
      setOrders,
      setSubscriptionInfo,
      setSyncMeta,
      syncMeta.lastSyncAt,
      updateClientCountOnServer,
      // syncFailures not needed in deps because we use functional update
    ]
  );

  useEffect(() => {
    let ignore = false;

    if (!authToken) {
      setAuthChecked(true);
      return;
    }
    console.info('[App] V�rification de la session utilisateur�');

    (async () => {
      try {
        const response = await fetch('/fedapay_integration/me.php', {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (ignore) {
          return;
        }

        if (response.ok) {
          const data = await readJsonResponse<any>(response);
          const subscription = data.user?.subscription ?? { ...defaultSubscription };
          setCurrentUser({ ...data.user, subscription });
          setSubscriptionInfo(subscription);
          console.info('[App] Session valide, utilisateur charg�.');
        } else if (response.status === 401) {
          console.warn('[App] Session expir�e (401).', await response.text());
          setAuthError("Votre session a expir�. Merci de vous reconnecter.");
          clearAuth(true);
        } else {
          const errorText = await response.text();
          console.error('[App] Erreur lors de la v�rification de session:', response.status, errorText);
          clearAuth(true);
        }
      } catch (error) {
        if (!ignore) {
          console.error('Erreur de verification de session:', error);
          clearAuth(true);
        }
      } finally {
        if (!ignore) {
          setAuthChecked(true);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [authToken, clearAuth]);

  useEffect(() => {
    if (!currentUser?.userId || !authToken) {
      return;
    }
    fetchSubscriptionStatus(currentUser.userId);
  }, [currentUser?.userId, authToken, fetchSubscriptionStatus]);

  // If user just returned from payment flow, poll subscription status until active or timeout
  useEffect(() => {
    if (!currentUser?.userId) return;

    let polling = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    try {
      const pendingRaw = localStorage.getItem('pendingPayment');
      if (pendingRaw) {
        const pending = JSON.parse(pendingRaw);
        if (pending && pending.userId === currentUser.userId) {
          polling = true;
        }
      }
    } catch (e) {
      // ignore JSON errors
    }

    if (!polling) return;

    let attempts = 0;
    const maxAttempts = 12; // ~ 12 * 1.5s = ~18s (augmenté de 8 à 12)

    const tryRefresh = async () => {
      attempts += 1;
      const active = await fetchSubscriptionStatus(currentUser.userId);
      if (active || attempts >= maxAttempts) {
        // stop polling
        if (intervalId) clearInterval(intervalId);
        try { localStorage.removeItem('pendingPayment'); } catch (e) { }
      }
    };

    // 🆕 SOLUTION 3: Commencer le polling après 200ms (laisse le temps au webhook)
    // au lieu de 3000ms d'attente avant première tentative
    setTimeout(() => {
      tryRefresh();
      intervalId = setInterval(tryRefresh, 1500); // 1.5s au lieu de 3s
    }, 200);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [currentUser?.userId, fetchSubscriptionStatus]);

  // Auto-refresh subscription when user returns to page (after payment flow)
  // With cooldown to prevent flickering from too-frequent refreshes
  useEffect(() => {
    if (!currentUser?.userId) return;

    let lastRefreshTime = 0;
    const REFRESH_COOLDOWN_MS = 30000; // 30 seconds minimum between refreshes

    const refreshWithCooldown = () => {
      const now = Date.now();
      if (now - lastRefreshTime >= REFRESH_COOLDOWN_MS) {
        lastRefreshTime = now;
        fetchSubscriptionStatus(currentUser.userId);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshWithCooldown();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also refresh on window focus (for browsers that don't fire visibilitychange)
    const handleFocus = () => {
      refreshWithCooldown();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [currentUser?.userId, fetchSubscriptionStatus]);

  useEffect(() => {
    if (!currentUser || !authToken) {
      return;
    }
    if (syncStatus === 'syncing') {
      return;
    }
    const intervalMs = SYNC_INTERVAL_DAYS * 24 * 60 * 60 * 1000;
    const lastSyncTime = syncMeta.lastSyncAt ? Date.parse(syncMeta.lastSyncAt) : 0;
    const now = Date.now();
    const dueToInterval = !syncMeta.lastSyncAt || now - lastSyncTime > intervalMs;
    const online = typeof navigator === 'undefined' ? true : navigator.onLine;

    if (online && (hasUnsyncedChanges || dueToInterval) && syncStatus !== 'error') {
      performSync();
    }
  }, [authToken, currentUser, hasUnsyncedChanges, performSync, syncMeta.lastSyncAt, syncStatus]);

  useEffect(() => {
    return () => {
      if (paymentRefreshTimeout.current) {
        clearTimeout(paymentRefreshTimeout.current);
      }
    };
  }, []);

  const handleSaveClient = async (clientData: ClientDraft) => {
    if (!currentUser) {
      setAuthError('Veuillez vous connecter pour ajouter des clients.');
      return;
    }

    if (clientData.id) {
      const updatedAt = new Date().toISOString();
      setClients((prevClients) => {
        const updatedClients = prevClients.map((client) => {
          if (client.id !== clientData.id) {
            return client;
          }
          return {
            ...client,
            ...clientData,
            updatedAt,
            isDeleted: client.isDeleted ?? false,
            measurements: client.measurements || [],
          };
        });
        if (selectedClient && selectedClient.id === clientData.id) {
          const refreshed = updatedClients.find((client) => client.id === clientData.id) || null;
          setSelectedClient(refreshed);
        }
        return updatedClients;
      });
      setClientToEdit(null);
      return;
    }

    if (
      effectiveSubscription.subscription_status === 'free' &&
      effectiveSubscription.client_count >= MAX_FREE_CLIENTS
    ) {
      const shouldUpgrade = window.confirm(
        `Limite de ${MAX_FREE_CLIENTS} clients gratuits atteinte. Passez au forfait superieur pour ajouter plus de clients. Aller a la page abonnement ?`
      );
      if (shouldUpgrade) {
        goToSubscriptionPage();
      }
      return;
    }
    if (effectiveSubscription.subscription_status === 'expired') {
      const shouldUpgrade = window.confirm(
        'Votre abonnement a expire. Veuillez le renouveler pour ajouter plus de clients. Aller a la page abonnement ?'
      );
      if (shouldUpgrade) {
        goToSubscriptionPage();
      }
      return;
    }

    // 🆕 SOLUTION 1: Valider côté serveur avant d'ajouter le client
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      const validateResponse = await fetch('/api/add_client.php', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userId: currentUser.userId,
          clientName: clientData.name,
        }),
      });

      const validateData = await validateResponse.json();

      if (!validateResponse.ok) {
        // Serveur rejette l'ajout
        const message = validateData.message || 'Impossible d\'ajouter ce client';
        const shouldUpgrade = window.confirm(`${message} Aller a la page abonnement ?`);
        if (shouldUpgrade) {
          goToSubscriptionPage();
        }
        return;
      }
    } catch (error) {
      console.error('Erreur validation serveur:', error);
      // Continuer quand même si erreur réseau (mais pas idéal)
      // alert('Erreur de connexion. Impossible de valider l\'ajout.');
      // return;
    }

    const newClient: Client = {
      name: clientData.name,
      gender: clientData.gender,
      contact: clientData.contact,
      id: crypto.randomUUID(),
      updatedAt: new Date().toISOString(),
      isDeleted: false,
      measurements: [],
      images: [],
    };

    const updatedClients = [...clients, newClient];
    setClients(updatedClients);
    setIsAddingClient(false);

    // Auto-select new client and open measurement form
    setSelectedClient(newClient);
    setIsTakingMeasurements(true);

    const newCount = updatedClients.filter((client) => !client.isDeleted).length;
    const nextSubscription: SubscriptionInfo = { ...effectiveSubscription, client_count: newCount };
    setSubscriptionInfo(nextSubscription);
    setCurrentUser((prev) => (prev ? { ...prev, subscription: nextSubscription } : prev));
    updateClientCountOnServer(newCount);
  };

  const handleDeleteClient = (clientId: string) => {
    const updatedAt = new Date().toISOString();
    const updatedClients = clients.map((client) => {
      if (client.id !== clientId) {
        return client;
      }
      const measurements = (client.measurements || []).map((measurement) => ({
        ...measurement,
        isDeleted: true,
        updatedAt,
      }));
      return {
        ...client,
        isDeleted: true,
        updatedAt,
        measurements,
      };
    });
    setClients(updatedClients);
    setSelectedClient(null);
    setOrders((prev) =>
      prev.map((order) =>
        order.clientId === clientId ? { ...order, isDeleted: true, updatedAt } : order
      )
    );

    const newCount = updatedClients.filter((client) => !client.isDeleted).length;
    const nextSubscription: SubscriptionInfo = { ...effectiveSubscription, client_count: newCount };
    setSubscriptionInfo(nextSubscription);
    setCurrentUser((prev) => (prev ? { ...prev, subscription: nextSubscription } : prev));
    updateClientCountOnServer(newCount);
  };

  const handleSetClientToEdit = (client: Client) => {
    setClientToEdit(client);
  };

  const handleSaveMeasurement = (measurementData: MeasurementDraft) => {
    if (!selectedClient) {
      return;
    }

    const updatedAt = new Date().toISOString();

    const { attachments: draftAttachments, date: draftDate, id: draftId, ...restDraft } = measurementData;
    const attachments = Array.isArray(draftAttachments) ? [...draftAttachments] : undefined;

    if (draftId) {
      const updatedMeasurements = selectedClient.measurements.map((measurement) => {
        if (measurement.id !== draftId) {
          return measurement;
        }
        const nextAttachments = attachments ?? measurement.attachments ?? [];
        return {
          ...measurement,
          ...restDraft,
          id: draftId,
          attachments: nextAttachments,
          updatedAt,
          date: toIsoString(draftDate ?? measurement.date ?? new Date()),
          isDeleted: measurement.isDeleted ?? false,
        };
      });
      const updatedClient: Client = {
        ...selectedClient,
        measurements: updatedMeasurements,
        updatedAt,
      };
      setClients((prev) => prev.map((client) => (client.id === selectedClient.id ? updatedClient : client)));
      setSelectedClient(updatedClient);
    } else {
      const measurementId = crypto.randomUUID();
      const nextAttachments = Array.isArray(attachments) ? attachments : [];
      const newMeasurement: Measurement = {
        ...restDraft,
        id: measurementId,
        outfitType: (restDraft.outfitType as OutfitType) ?? OutfitType.Haut,
        attachments: nextAttachments,
        date: toIsoString(draftDate ?? new Date()),
        updatedAt,
        isDeleted: false,
      };
      const updatedClient = {
        ...selectedClient,
        measurements: [...selectedClient.measurements, newMeasurement],
        updatedAt,
      };
      setClients((prev) => prev.map((client) => (client.id === selectedClient.id ? updatedClient : client)));
      setSelectedClient(updatedClient);
    }

    setIsTakingMeasurements(false);
    setMeasurementToEdit(null);
  };

  const handleDeleteMeasurement = (measurementId: string) => {
    if (!selectedClient) {
      return;
    }
    const updatedAt = new Date().toISOString();
    const updatedMeasurements = selectedClient.measurements.map((measurement) => {
      if (measurement.id !== measurementId) {
        return measurement;
      }
      return {
        ...measurement,
        isDeleted: true,
        updatedAt,
      };
    });
    const updatedClient: Client = {
      ...selectedClient,
      measurements: updatedMeasurements,
      updatedAt,
    };
    setClients((prev) => prev.map((client) => (client.id === selectedClient.id ? updatedClient : client)));
    setSelectedClient(updatedClient);
  };

  const handleSetMeasurementToEdit = (measurement: Measurement | null) => {
    setMeasurementToEdit(measurement);
    setIsTakingMeasurements(true);
  };

  useEffect(() => {
    if (!selectedClient) {
      return;
    }
    const refreshed = clients.find((client) => client.id === selectedClient.id && !client.isDeleted);
    if (!refreshed) {
      setSelectedClient(null);
    } else if (refreshed !== selectedClient) {
      setSelectedClient(refreshed);
    }
  }, [clients, selectedClient]);

  const closeForms = () => {
    setIsAddingClient(false);
    setIsTakingMeasurements(false);
    setMeasurementToEdit(null);
    setClientToEdit(null);
  };

  const handleLogout = useCallback(async () => {
    try {
      if (authToken) {
        await fetch('/fedapay_integration/logout.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: authToken }),
        });
      }
    } catch (error) {
      console.error('Erreur lors de la deconnexion:', error);
    } finally {
      clearAuth(true);
    }
  }, [authToken, clearAuth]);

  const handleUpdateProfile = useCallback(async (updates: { email?: string; full_name?: string }) => {
    if (!authToken) {
      throw new Error('Non authentifié');
    }
    const response = await fetch('/fedapay_integration/update_profile.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(updates),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || data?.status !== 'ok') {
      throw new Error(data?.message || 'Erreur lors de la mise à jour');
    }
    // Update local user state
    if (currentUser && data.user) {
      setCurrentUser({
        ...currentUser,
        email: data.user.email ?? currentUser.email,
        full_name: data.user.full_name ?? currentUser.full_name,
      });
    }
  }, [authToken, currentUser]);

  const handleLogin = useCallback(
    async ({ identifier, password }: { identifier: string; password: string }) => {
      setAuthLoading(true);
      setAuthError(null);
      try {
        const authPayload = await loginWithConfiguredProvider({ identifier, password });
        applyAuth(authPayload);
        await fetchSubscriptionStatus(authPayload.user.userId);
        setAuthChecked(true);
      } catch (error) {
        console.error('Erreur de connexion:', error);
        setAuthError(error instanceof Error ? error.message : 'Connexion impossible.');
      } finally {
        setAuthLoading(false);
      }
    },
    [applyAuth, fetchSubscriptionStatus]
  );

  const handleRegister = useCallback(
    async ({ fullName, phone, email, password }: { fullName: string; phone: string; email?: string; password: string }) => {
      setAuthLoading(true);
      setAuthError(null);
      try {
        const authPayload = await registerWithConfiguredProvider({ fullName, phone, email, password });
        applyAuth(authPayload);
        await fetchSubscriptionStatus(authPayload.user.userId);
        setAuthChecked(true);
      } catch (error) {
        console.error('Erreur lors de l’inscription:', error);
        setAuthError(error instanceof Error ? error.message : "Inscription impossible.");
      } finally {
        setAuthLoading(false);
      }
    },
    [applyAuth, fetchSubscriptionStatus]
  );

  // Affichages d'authentification sécurisés pour éviter tout accès à un currentUser null
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center text-brand-dark">
        Chargement...
      </div>
    );
  }

  if (!currentUser) {
    return (
      <AuthView
        onLogin={handleLogin}
        onRegister={handleRegister}
        authProvider={configuredAuthProvider}
        loading={authLoading}
        error={authError}
        deferredPrompt={deferredInstallPromptRef.current}
        isInstallPromptAvailable={isInstallPromptAvailable}
        onInstallClick={handleInstallClick}
      />
    );
  }

  return (
    <div className="t-page flex flex-col font-sans pb-20 md:pb-0">
      <InstallBanner
        deferredPrompt={deferredInstallPromptRef.current}
        isInstallPromptAvailable={isInstallPromptAvailable}
        isStandalone={isStandalone}
        onInstallClick={handleInstallClick}
      />

      <AppHeader
        currentUser={currentUser}
        selectedClient={selectedClient}
        syncStatus={syncStatus}
        syncError={syncError}
        syncMeta={syncMeta}
        pendingUploads={{}} // Empty object as fallback
        effectiveSubscription={effectiveSubscription}
        subscriptionPlan={subscriptionPlan}
        activeClientCount={activeClientCount}
        remainingClients={remainingClients}
        PLAN_LABELS={PLAN_LABELS}
        PLAN_CLIENT_LIMITS={PLAN_CLIENT_LIMITS}
        actualSubscriptionStatus={actualSubscriptionStatus}
        safeUserId={safeUserId}
        canUpgradeEarly={canUpgradeEarly}
        ordersEnabled={ordersEnabled}
        showInstallCta={showInstallCta}
        deferredPrompt={deferredInstallPromptRef.current}
        isInstallPromptAvailable={isInstallPromptAvailable}
        isStandalone={isStandalone}
        showInstallModal={showInstallModal}
        setShowInstallModal={setShowInstallModal}
        triggerPwaInstall={handleInstallClick}
        activeTab={activeTab}
        onSetActiveTab={(tab) => {
          setActiveTab(tab);
          // Réinitialiser toutes les vues spéciales lors du changement d'onglet
          setShowDashboard(tab === 'dashboard');
          setShowProfileSettings(tab === 'profile');
          setShowAbout(tab === 'about');
          if (tab !== 'clients') setSelectedClient(null);
        }}
        onSetSelectedClient={setSelectedClient}
        onLogout={handleLogout}
        onInstallClick={handleInstallClick}
        onShowProfileSettings={() => setShowProfileSettings(true)}
        onPerformSync={performSync}
      />

      {showInstallInstructions && (
        <div className="mx-auto mt-2 w-full max-w-5xl px-4">
          <div className="rounded-xl border border-brand-accent bg-white/90 px-4 py-3 text-sm text-brand-dark shadow">
            <div className="flex items-start gap-3">
              <p className="flex-1">
                Pour installer l'application sur votre iPhone ou iPad, ouvrez le menu "Partager", puis choisissez
                "Sur l'ecran d'accueil".
              </p>
              <button
                type="button"
                onClick={closeInstallInstructions}
                className="text-xs font-semibold text-brand-accent transition hover:underline"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 mx-auto w-full max-w-5xl overflow-y-auto">
        {activeTab === 'dashboard' || showDashboard ? (
          <Dashboard
            clients={activeClients}
            orders={activeOrders}
            onClose={() => { setShowDashboard(false); setActiveTab('clients'); }}
          />
        ) : activeTab === 'profile' || showProfileSettings ? (
          currentUser && (
            <ProfileSettings
              user={currentUser}
              onClose={() => { setShowProfileSettings(false); setActiveTab('clients'); }}
              onSave={handleUpdateProfile}
              onUploadLogo={uploadFileForCurrentUser}
              isEmbedded={true}
              isDark={isDark}
              onToggleTheme={toggleTheme}
            />
          )
        ) : activeTab === 'about' || showAbout ? (
          <AboutPage onClose={() => { setShowAbout(false); setActiveTab('clients'); }} />
        ) : activeTab === 'gallery' ? (
          <GalleryView
            clients={activeClients}
            orders={activeOrders}
            onSelectImage={(_url, _name, clientId) => {
              const client = activeClients.find(c => c.id === clientId);
              if (client) {
                setSelectedClient(client);
                setActiveTab('clients');
              }
            }}
          />
        ) : activeTab === 'orders' ? (
          <OrderListView
            orders={activeOrders}
            clients={activeClients}
            onSelectOrder={(client, orderId) => {
              setSelectedClient(client);
              setActiveTab('clients');
            }}
          />
        ) : !selectedClient ? (
          <>
            <div className="px-4 pt-6 md:px-6">
              <div className="relative mb-4">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <SearchIcon className="h-5 w-5 t-text-muted" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Rechercher un client..."
                  className="t-input pl-10 h-12"
                  aria-label="Rechercher un client par nom ou contact"
                />
              </div>
              <FilterChips activeFilter={activeFilter} onFilterChange={setActiveFilter} counts={filterCounts} />
            </div>
            <ClientList
              clients={filteredClients}
              onSelectClient={setSelectedClient}
              searchQuery={searchQuery}
              orders={activeOrders}
            />
          </>
        ) : selectedClientForView ? (
          <ClientDetails
            client={selectedClientForView}
            onBack={() => setSelectedClient(null)}
            onSetMeasurementToEdit={handleSetMeasurementToEdit}
            onDeleteMeasurement={handleDeleteMeasurement}
            onDeleteClient={handleDeleteClient}
            onEditClient={handleSetClientToEdit}
            onAddMeasurement={() => setIsTakingMeasurements(true)}
            orders={ordersForSelectedClient}
            ordersEnabled={ordersEnabled}
            onSaveOrder={handleSaveOrder}
            onDeleteOrder={handleDeleteOrder}
            onChangeOrderStatus={handleChangeOrderStatus}
            onToggleOrderPaid={handleToggleOrderPaid}
            onUploadClientImage={handleAddClientImage}
            onRemoveClientImage={handleRemoveClientImage}
            onSetClientProfileImage={() => { }}
            userPlan={subscriptionInfo?.plan}
            currentUser={currentUser}
            authToken={authToken}
          />
        ) : null}
      </main>

      <BottomNav
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          setShowDashboard(tab === 'dashboard');
          setShowProfileSettings(tab === 'profile');
          setShowAbout(tab === 'about');
          if (tab !== 'clients' && tab !== 'dashboard') {
            setSelectedClient(null);
          }
        }}
      />

      <div className="fixed bottom-[calc(5rem+var(--sab))] right-4 z-60 md:bottom-8 md:right-8">
        {selectedClient ? (
          <button
            onClick={() => setIsTakingMeasurements(true)}
            className="rounded-full bg-brand-accent p-4 text-white shadow-lg transition hover:bg-brand-secondary focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2"
            aria-label="Prendre des mesures"
          >
            <PlusIcon className="h-8 w-8" />
          </button>
        ) : (
          <div className="flex flex-col items-end gap-3">
            {subscriptionLoading && <p className="text-sm text-gray-500">Chargement abonnement...</p>}
            {subscriptionError && <p className="text-sm text-red-500">{subscriptionError}</p>}
            {!needsSubscriptionPayment && (
              <div className="flex flex-col items-end gap-2 text-right">
                <p className="text-xs text-gray-500">
                  Ajoutez des clients
                </p>
              </div>
            )}
            {needsSubscriptionPayment && (
              <div className="rounded-xl bg-white/80 px-4 py-3 text-right shadow">
                <p className="text-sm font-medium text-red-600">
                  {actualSubscriptionStatus === 'expired'
                    ? 'Abonnement expiré. Renouvelez pour ajouter de nouveaux clients.'
                    : `Limite de ${MAX_FREE_CLIENTS} clients gratuits atteinte. Passez au forfait superieur.`}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {actualSubscriptionStatus === 'expired'
                    ? 'Vos clients existants restent accessibles.'
                    : ''}
                </p>
                <div className="mt-3 flex justify-end">
                  {safeUserId && (
                    <a
                      href="/abonnement/"
                      className="bg-brand-accent text-white px-4 py-2 rounded-md hover:bg-brand-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent"
                    >
                      {actualSubscriptionStatus === 'expired' ? 'Renouveler' : "S'abonner maintenant"}
                    </a>
                  )}
                </div>
              </div>
            )}
            <button
              onClick={() => setIsAddingClient(true)}
              disabled={needsSubscriptionPayment}
              className="rounded-full bg-brand-accent p-4 text-white shadow-lg transition hover:bg-brand-secondary focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Ajouter un client"
            >
              <PlusIcon className="h-8 w-8" />
            </button>
          </div>
        )}
      </div>

      {(isAddingClient || clientToEdit) && (
        <ClientForm onSave={handleSaveClient} onClose={closeForms} existingClient={clientToEdit} />
      )}
      {(isTakingMeasurements || measurementToEdit) && (
        <MeasurementForm
          onSave={handleSaveMeasurement}
          onClose={closeForms}
          existingMeasurement={measurementToEdit || undefined}
          onUploadAttachment={uploadFileForCurrentUser}
          onDeleteAttachment={deleteFileForCurrentUser}
        />
      )}

      {/* Welcome Modal - shown only on first visit */}
      {showWelcomeModal && (
        <WelcomeModal
          onClose={() => {
            localStorage.setItem('cms-welcome-shown', 'true');
            setShowWelcomeModal(false);
          }}
        />
      )}

      {/* Administrateur Dashboard */}
      {showAdmin && (
        <AdminDashboard onClose={() => {
          setShowAdmin(false);
          // Optional: remove query param
          const url = new URL(window.location.href);
          url.searchParams.delete('admin');
          window.history.replaceState({}, '', url);
        }} />
      )}
    </div>
  );
};

export default App;


